import {
  qvdAnalysisRunResponseSchema,
  startQvdAnalysisResponseSchema,
  qvdScopeOptionsResponseSchema,
  listQvdAnalysisRunsResponseSchema,
  listOrphanQvdsResponseSchema,
  listUnloadedColumnsResponseSchema,
  listQvdFilesResponseSchema,
  qvdDirectoryResponseSchema,
  listQvdRunLogsResponseSchema,
  qvdConsumingAppsResponseSchema,
  qvdRunCompareResponseSchema,
  type StartQvdAnalysisInput,
  type QvdAnalysisRun,
  type QvdScopeOptions,
  type OrphanQvd,
  type UnloadedColumnsQvd,
  type QvdFile,
  type QvdFilesSort,
  type QvdDirectoryResponse,
  type QvdRunDiffSide,
  type QvdRunDiffSummary,
  type QvdRunLog,
  type QvdRunLogLevel,
  type QvdConsumingAppsResponse,
} from "@repo/shared";

import {
  API_V1_PREFIX,
  apiRequest,
  apiRequestJson,
  apiDownload,
  parseOrThrow,
} from "../api-client";

const BASE = `${API_V1_PREFIX}/qvd-analyses`;

export function downloadQvdExport(
  runId: string,
  type: "orphans" | "columns",
  format: "csv" | "json",
): Promise<void> {
  return apiDownload(
    `${BASE}/${runId}/export?type=${type}&format=${format}`,
    `qvd-${type}-${runId}.${format}`,
  );
}

export function downloadQvdRunLogs(runId: string): Promise<void> {
  return apiDownload(`${BASE}/${runId}/logs/export`, `qvd-logs-${runId}.txt`);
}

export async function startQvdAnalysis(
  input: StartQvdAnalysisInput,
): Promise<{ run: QvdAnalysisRun; created: boolean }> {
  const { payload } = await apiRequest({
    path: BASE,
    method: "POST",
    body: input,
  });
  const res = parseOrThrow(payload, startQvdAnalysisResponseSchema, "Failed to start QVD analysis");
  return { run: res.data, created: res.created };
}

export async function getQvdScopeOptions(connectionId: string): Promise<QvdScopeOptions> {
  const res = await apiRequestJson({
    path: `${BASE}/scope-options?connectionId=${encodeURIComponent(connectionId)}`,
    responseSchema: qvdScopeOptionsResponseSchema,
    badResponseMessage: "Failed to load scope options",
  });
  return res.data;
}

export async function getQvdAnalysisRun(id: string): Promise<QvdAnalysisRun> {
  const res = await apiRequestJson({
    path: `${BASE}/${id}`,
    responseSchema: qvdAnalysisRunResponseSchema,
    badResponseMessage: "Failed to load QVD analysis run",
  });
  return res.data;
}

export async function listQvdAnalysisRuns(params?: {
  connectionId?: string;
  limit?: number;
  cursor?: string;
}): Promise<{ data: QvdAnalysisRun[]; nextCursor?: string }> {
  const search = new URLSearchParams();
  if (params?.connectionId) search.set("connectionId", params.connectionId);
  if (params?.limit !== undefined) search.set("limit", String(params.limit));
  if (params?.cursor) search.set("cursor", params.cursor);
  const qs = search.size > 0 ? `?${search.toString()}` : "";
  return apiRequestJson({
    path: `${BASE}${qs}`,
    responseSchema: listQvdAnalysisRunsResponseSchema,
    badResponseMessage: "Failed to list QVD analysis runs",
  });
}

export async function listQvdOrphans(
  runId: string,
  params?: { limit?: number; cursor?: string },
): Promise<{ data: OrphanQvd[]; nextCursor?: string }> {
  const search = new URLSearchParams();
  if (params?.limit !== undefined) search.set("limit", String(params.limit));
  if (params?.cursor) search.set("cursor", params.cursor);
  const qs = search.size > 0 ? `?${search.toString()}` : "";
  return apiRequestJson({
    path: `${BASE}/${runId}/orphans${qs}`,
    responseSchema: listOrphanQvdsResponseSchema,
    badResponseMessage: "Failed to load orphan QVDs",
  });
}

// Two disjoint populations behind one route: the QVDs with a column verdict
// (default) and the ones whose verdict was withheld for lack of evidence
// (`evidence: "incomplete"`). Same row shape either way.
export async function listQvdUnloadedColumns(
  runId: string,
  params?: { limit?: number; cursor?: string; evidence?: "complete" | "incomplete" },
): Promise<{ data: UnloadedColumnsQvd[]; nextCursor?: string }> {
  const search = new URLSearchParams();
  if (params?.limit !== undefined) search.set("limit", String(params.limit));
  if (params?.cursor) search.set("cursor", params.cursor);
  if (params?.evidence) search.set("evidence", params.evidence);
  const qs = search.size > 0 ? `?${search.toString()}` : "";
  return apiRequestJson({
    path: `${BASE}/${runId}/columns${qs}`,
    responseSchema: listUnloadedColumnsResponseSchema,
    badResponseMessage: "Failed to load unloaded columns",
  });
}

// Per-QVD detail for one unloaded-columns row: the apps consuming the file and
// the fields each of them loads. Detail-only — it never rides on the list rows.
export async function getQvdConsumingApps(
  runId: string,
  rowId: string,
): Promise<QvdConsumingAppsResponse> {
  return apiRequestJson({
    path: `${BASE}/${runId}/columns/${rowId}`,
    responseSchema: qvdConsumingAppsResponseSchema,
    badResponseMessage: "Failed to load consuming apps",
  });
}

// Same detail payload for one row of the FILE inventory. `fields` always comes
// back empty here: per-field evidence is the intersection with the QVD header,
// which only the column phase reads.
export async function getQvdFileConsumingApps(
  runId: string,
  fileId: string,
): Promise<QvdConsumingAppsResponse> {
  return apiRequestJson({
    path: `${BASE}/${runId}/files/${fileId}`,
    responseSchema: qvdConsumingAppsResponseSchema,
    badResponseMessage: "Failed to load consuming apps",
  });
}

export async function listQvdFiles(
  runId: string,
  params?: {
    limit?: number;
    cursor?: string;
    sort?: QvdFilesSort;
    search?: string;
    space?: string;
  },
): Promise<{ data: QvdFile[]; nextCursor?: string }> {
  const search = new URLSearchParams();
  if (params?.limit !== undefined) search.set("limit", String(params.limit));
  if (params?.cursor) search.set("cursor", params.cursor);
  if (params?.sort) search.set("sort", params.sort);
  if (params?.search) search.set("search", params.search);
  if (params?.space) search.set("space", params.space);
  const qs = search.size > 0 ? `?${search.toString()}` : "";
  return apiRequestJson({
    path: `${BASE}/${runId}/files${qs}`,
    responseSchema: listQvdFilesResponseSchema,
    badResponseMessage: "Failed to load files",
  });
}

// One level of a space's directory tree, aggregated server-side. `prefix` is ""
// for the space root, otherwise a path ending in "/".
export async function listQvdDirectory(
  runId: string,
  params: { space: string; prefix?: string },
): Promise<QvdDirectoryResponse> {
  const search = new URLSearchParams({ space: params.space });
  if (params.prefix) search.set("prefix", params.prefix);
  return apiRequestJson({
    path: `${BASE}/${runId}/files/directories?${search.toString()}`,
    responseSchema: qvdDirectoryResponseSchema,
    badResponseMessage: "Failed to load directories",
  });
}

// On-demand set diff against another completed run of the same connection +
// scope. The service orders the pair by finish time, so the ids may be passed in
// either order — `from` in the payload is always the older run.
export async function compareQvdRuns(runId: string, baseId: string): Promise<QvdRunDiffSummary> {
  const res = await apiRequestJson({
    path: `${BASE}/${runId}/compare?baseId=${encodeURIComponent(baseId)}`,
    responseSchema: qvdRunCompareResponseSchema,
    badResponseMessage: "Failed to compare QVD analysis runs",
  });
  return res.data;
}

// One side of that diff, paginated with the same filters/cursors as /files.
export async function listQvdDiffFiles(
  runId: string,
  baseId: string,
  side: QvdRunDiffSide,
  params?: {
    limit?: number;
    cursor?: string;
    sort?: QvdFilesSort;
    search?: string;
    space?: string;
  },
): Promise<{ data: QvdFile[]; nextCursor?: string }> {
  const search = new URLSearchParams({ baseId, side });
  if (params?.limit !== undefined) search.set("limit", String(params.limit));
  if (params?.cursor) search.set("cursor", params.cursor);
  if (params?.sort) search.set("sort", params.sort);
  if (params?.search) search.set("search", params.search);
  if (params?.space) search.set("space", params.space);
  return apiRequestJson({
    path: `${BASE}/${runId}/compare/files?${search.toString()}`,
    responseSchema: listQvdFilesResponseSchema,
    badResponseMessage: "Failed to load compared files",
  });
}

export async function listQvdRunLogs(
  runId: string,
  params?: { limit?: number; cursor?: string; level?: QvdRunLogLevel },
): Promise<{ data: QvdRunLog[]; nextCursor?: string }> {
  const search = new URLSearchParams();
  if (params?.limit !== undefined) search.set("limit", String(params.limit));
  if (params?.cursor) search.set("cursor", params.cursor);
  if (params?.level) search.set("level", params.level);
  const qs = search.size > 0 ? `?${search.toString()}` : "";
  return apiRequestJson({
    path: `${BASE}/${runId}/logs${qs}`,
    responseSchema: listQvdRunLogsResponseSchema,
    badResponseMessage: "Failed to load run logs",
  });
}

export async function cancelQvdAnalysisRun(id: string): Promise<QvdAnalysisRun> {
  const res = await apiRequestJson({
    path: `${BASE}/${id}/cancel`,
    method: "POST",
    responseSchema: qvdAnalysisRunResponseSchema,
    badResponseMessage: "Failed to cancel QVD analysis",
  });
  return res.data;
}

export async function reprocessQvdAnalysis(id: string): Promise<QvdAnalysisRun> {
  const res = await apiRequestJson({
    path: `${BASE}/${id}/reprocess`,
    method: "POST",
    responseSchema: qvdAnalysisRunResponseSchema,
    badResponseMessage: "Failed to reprocess QVD analysis",
  });
  return res.data;
}

export async function resumeQvdAnalysis(id: string): Promise<QvdAnalysisRun> {
  const res = await apiRequestJson({
    path: `${BASE}/${id}/resume`,
    method: "POST",
    responseSchema: qvdAnalysisRunResponseSchema,
    badResponseMessage: "Failed to resume QVD analysis",
  });
  return res.data;
}

export async function deleteQvdAnalysisRun(id: string): Promise<void> {
  await apiRequest({
    path: `${BASE}/${id}`,
    method: "DELETE",
    expectedStatus: 204,
  });
}
